
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataLoader } from "../components/DataLoader";
import { useMembersData } from "./members/hooks/useMembersData";
import { useMemberFilters } from "./members/hooks/useMemberFilters";
import { useSortMembers } from "./hooks/useSortMembers";
import { usePagination } from "./hooks/usePagination";
import { MembershipTypeFilter } from "./members/MembershipTypeFilter";
import { PaymentStatusFilter } from "./members/PaymentStatusFilter";
import { MembersTable } from "./members/MembersTable";
import { DateRangeFilter } from "./members/DateRangeFilter";
import { SearchBar } from "./members/SearchBar";
import { ExportButtons } from "./members/ExportButtons";
import { TablePagination } from "./members/TablePagination";

export const MembersTab = () => {
  // Fetch members data
  const { members, isLoading, error } = useMembersData();
  
  // Filter members
  const { 
    selectedTypes, 
    selectedPaymentStatuses,
    startDate,
    endDate,
    searchTerm,
    filteredMembers, 
    handleTypeChange, 
    handlePaymentStatusChange,
    handleStartDateChange,
    handleEndDateChange,
    handleSearchChange
  } = useMemberFilters(members);
  
  // Sort members
  const { sortedData, sortConfig, sortData } = useSortMembers(filteredMembers);

  // Paginate members
  const ITEMS_PER_PAGE = 20;
  const { 
    currentPage, 
    totalPages, 
    paginatedData, 
    goToPage, 
    nextPage, 
    prevPage 
  } = usePagination({ 
    data: sortedData, 
    itemsPerPage: ITEMS_PER_PAGE 
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Membres</CardTitle>
          <CardDescription>Liste des membres</CardDescription>
        </div>
        <ExportButtons members={sortedData} />
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-4">
          <div className="w-full md:w-1/2">
            <SearchBar 
              searchTerm={searchTerm} 
              onSearchChange={handleSearchChange} 
              placeholder="Rechercher par nom, prénom ou email..." 
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MembershipTypeFilter 
              selectedTypes={selectedTypes} 
              onTypeChange={handleTypeChange} 
            />
            
            <PaymentStatusFilter 
              selectedStatuses={selectedPaymentStatuses} 
              onStatusChange={handlePaymentStatusChange} 
            />
            
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          </div>
        </div>

        <DataLoader
          isLoading={isLoading}
          error={error}
          isEmpty={sortedData.length === 0}
          emptyMessage="Aucun membre trouvé"
        >
          <>
            <MembersTable 
              members={paginatedData} 
              sortConfig={sortConfig} 
              onSort={sortData} 
            />
            
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-muted-foreground">
                Affichage de {paginatedData.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0} à {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} sur {sortedData.length} membres
              </div>
              
              <TablePagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          </>
        </DataLoader>
      </CardContent>
    </Card>
  );
};
